import type { MutableRefObject } from 'react';
import React, { useState } from 'react';

import { type SEARCH_FIELD_PATH, SEARCH_FIELD_PATHS, SearchMode, SearchType } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Find } from 'cpool-tables/backend-resolvers';
import { objectKeys } from 'cpool-utils';

import { useFind } from '../../hooks/useFind';
import { createRequiredContext } from '~/utils/createRequiredContext';

const searchFieldKeys = objectKeys(SEARCH_FIELD_PATHS);
export type SearchField = Record<string, string | null>;

export type SearchContext = {
    searchQuery: string | null;
    searchFields: SearchField;
    editingSearchField: boolean;
    editingKey: string | null;
    flyoutOpen: boolean;
    lastTagHighlighted: boolean;
    searchResult: SchemaLoanRequestResolver_Find;
    searchMode: SearchMode;
    searchType: SearchType;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    setSearchQuery: (query: string | null) => void;
    setFlyoutOpen: (open: boolean) => void;
    setLastTagHighlighted: (highlighted: boolean) => void;
    handleAddSearchField: (key: string) => void;
    handleClose: () => void;
    handleSearchFields: (inputRef: HTMLInputElement | null) => void;
    handleTagCancel: (key: string, isEmpty: boolean, inputHandle: MutableRefObject<HTMLInputElement | null>) => void;
    handleTagDelete: (key: string) => void;
    handleTagEdit: (key: string) => void;
    handleTagSave: (key: string, value: string, inputHandle: MutableRefObject<HTMLInputElement | null>) => void;
    handleHighlightChange?: (highlighted: boolean) => void;
    switchSearchMode: () => void;
    setSearchType: (type: SearchType) => void;
};

const [useSearch, ContextProvider] = createRequiredContext<SearchContext>('SearchContext');

const SearchProvider = ({ children }: { children: React.ReactNode }) => {
    const [flyoutOpen, setFlyoutOpen] = useState(false);
    const [editingSearchField, setEditingSearchField] = useState<boolean>(false);
    const [lastTagHighlighted, setLastTagHighlighted] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [searchFields, setSearchFields] = useState<SearchField>({});
    const [searchMode, setSearchMode] = useState<SearchMode>(SearchMode.AND);
    const [searchType, setSearchType] = useState<SearchType>(SearchType.USER);

    const searchResult = useFind({ searchFields, searchMode, searchQuery, searchType });

    const handleClose = () => {
        setFlyoutOpen(false);
    };

    const switchSearchMode = () => {
        setSearchMode((prev) => (prev === SearchMode.AND ? SearchMode.OR : SearchMode.AND));
    };

    const handleSearchFields = (inputElement: HTMLInputElement | null) => {
        if (searchQuery === null || searchQuery.trim().length === 0) {
            return;
        }

        const query = searchQuery.trim().toLocaleLowerCase() as keyof SEARCH_FIELD_PATH;

        if (searchFieldKeys.includes(query)) {
            if (inputElement) {
                // eslint-disable-next-line no-param-reassign
                inputElement.value = '';
            }

            setSearchFields((prev) => ({
                ...prev,
                [query]: null,
            }));

            setSearchQuery(null);
            setEditingKey(query);
            setEditingSearchField(true);
        }
    };

    const handleTagEdit = (key: string) => {
        setSearchFields((prev) => ({
            ...prev,
            [key]: null,
        }));
        setEditingKey(key);
        setEditingSearchField(true);
    };

    const handleTagDelete = (key: string) => {
        const newSearchFields = { ...searchFields };
        delete newSearchFields[key];
        setSearchFields(newSearchFields);
    };

    const handleTagSave = (key: string, value: string, inputRef: MutableRefObject<HTMLInputElement | null>) => {
        setSearchFields((prev) => ({
            ...prev,
            [key]: value,
        }));
        setEditingSearchField(false);
        setEditingKey(null);

        inputRef.current?.focus();
    };

    const handleTagCancel = (key: string, isEmpty: boolean, inputRef: MutableRefObject<HTMLInputElement | null>) => {
        if (isEmpty) {
            const newSearchFields = { ...searchFields };
            delete newSearchFields[key];
            setSearchFields(newSearchFields);
        }

        setEditingSearchField(false);
        setEditingKey(null);

        inputRef.current?.focus();
    };

    const handleAddSearchField = (key: string) => {
        setSearchFields((prev) => ({
            ...prev,
            [key]: null,
        }));
        setEditingKey(key);
        setEditingSearchField(true);
    };

    const value = {
        editingKey,
        editingSearchField,
        fetchNextPage: searchResult.fetchNextPage,
        flyoutOpen,
        handleAddSearchField,
        handleClose,
        handleSearchFields,
        handleTagCancel,
        handleTagDelete,
        handleTagEdit,
        handleTagSave,
        hasNextPage: searchResult.hasNextPage,
        isFetching: searchResult.isFetching,
        isFetchingNextPage: searchResult.isFetchingNextPage,
        lastTagHighlighted,
        searchFields,
        searchMode,
        searchQuery,
        searchResult: searchResult.data,
        searchType,
        setFlyoutOpen,
        setLastTagHighlighted,
        setSearchQuery,
        setSearchType,
        switchSearchMode,
    };
    return <ContextProvider value={value}>{children}</ContextProvider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export { searchFieldKeys, SearchProvider, useSearch };
